// BotAudioTest — Day 5. Browser ↔ /api/dialer/v1/ws/bot-test/:sessionId.
//
// Captures mic via getUserMedia + AudioWorklet (PCM16 24kHz mono), streams
// frames over a WS, receives PCM16 24kHz frames back from ElevenLabs, plays
// them via Web Audio. Cost ticker shown at the bottom; server force-closes
// when the $0.50 cap is reached.

import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Loader2, X, Volume2 } from 'lucide-react';

// Inlined audio worklet: float32 → int16 + post chunks every ~20ms.
const WORKLET_CODE = `
class Pcm16Capture extends AudioWorkletProcessor {
  constructor() {
    super();
    this._buf = new Int16Array(0);
  }
  process(inputs) {
    const ch = inputs[0]?.[0];
    if (!ch || ch.length === 0) return true;
    const i16 = new Int16Array(ch.length);
    for (let i = 0; i < ch.length; i++) {
      const s = Math.max(-1, Math.min(1, ch[i]));
      i16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    // Concat to internal buffer; flush every ~480 samples (20ms @ 24kHz).
    const merged = new Int16Array(this._buf.length + i16.length);
    merged.set(this._buf);
    merged.set(i16, this._buf.length);
    this._buf = merged;
    const TARGET = 480;
    while (this._buf.length >= TARGET) {
      const chunk = this._buf.slice(0, TARGET);
      this._buf = this._buf.slice(TARGET);
      this.port.postMessage(chunk.buffer, [chunk.buffer]);
    }
    return true;
  }
}
registerProcessor('pcm16-capture', Pcm16Capture);
`;

export default function BotAudioTest({ botId, onClose }) {
  const [status, setStatus] = useState('idle'); // idle | connecting | live | error | closed
  const [error, setError] = useState('');
  const [cost, setCost] = useState({ spentUsd: 0, capUsd: 0.5 });
  const [transcript, setTranscript] = useState([]);
  const wsRef = useRef(null);
  const ctxRef = useRef(null);
  const nextStartRef = useRef(0);
  const mediaStreamRef = useRef(null);
  // Guard flag: prevents double-close when useEffect cleanup fires twice
  // (React 18 strict-mode) or when stop() races with ws.onclose.
  const stoppingRef = useRef(false);

  async function start() {
    setStatus('connecting');
    try {
      const { apiPost } = await import('../../lib/api.js');
      const r = await apiPost(`/dialer/v1/bots/${botId}/test-audio/start`, {});
      const { sessionId, token } = r?.data || {};
      if (!sessionId || !token) throw new Error('No session');

      const apiBase = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');
      const isAbs = /^https?:\/\//i.test(apiBase);
      const wsUrl = isAbs
        ? apiBase.replace(/^http/i, 'ws') + `/dialer/v1/ws/bot-test/${sessionId}?token=${encodeURIComponent(token)}`
        : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${apiBase}/dialer/v1/ws/bot-test/${sessionId}?token=${encodeURIComponent(token)}`;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx({ sampleRate: 24000 });
      ctxRef.current = ctx;
      await ctx.resume();
      // Worklet module from blob.
      const blob = new Blob([WORKLET_CODE], { type: 'application/javascript' });
      await ctx.audioWorklet.addModule(URL.createObjectURL(blob));

      // Mic capture.
      const media = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 24000, echoCancellation: true, noiseSuppression: true },
        video: false,
      });
      mediaStreamRef.current = media;
      const src = ctx.createMediaStreamSource(media);
      const node = new AudioWorkletNode(ctx, 'pcm16-capture');

      const ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      node.port.onmessage = (ev) => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send(ev.data); } catch (_) {}
        }
      };
      src.connect(node);

      ws.onopen = () => setStatus('live');
      ws.onerror = () => { setStatus('error'); setError('Connection lost'); };
      ws.onclose = () => setStatus('closed');
      ws.onmessage = (ev) => {
        if (typeof ev.data === 'string') {
          let msg;
          try { msg = JSON.parse(ev.data); } catch { return; }
          if (msg.type === 'cost') setCost({ spentUsd: msg.spentUsd, capUsd: msg.capUsd });
          if (msg.type === 'transcript') {
            setTranscript((t) => [...t.slice(-12), { role: msg.role, text: msg.text }]);
          }
          if (msg.type === 'end') {
            setStatus('closed');
            setError(msg.reason || '');
          }
          return;
        }
        // Binary PCM16 24kHz.
        const data = new Int16Array(ev.data);
        const pcm = new Float32Array(data.length);
        for (let i = 0; i < data.length; i++) pcm[i] = data[i] / 32768;
        playPcm(pcm);
      };

      function playPcm(pcm) {
        const cur = ctxRef.current;
        if (!cur || cur.state === 'closed') return;
        const buf = cur.createBuffer(1, pcm.length, 24000);
        buf.getChannelData(0).set(pcm);
        const s = cur.createBufferSource();
        s.buffer = buf;
        s.connect(cur.destination);
        const startAt = Math.max(cur.currentTime + 0.05, nextStartRef.current);
        s.start(startAt);
        nextStartRef.current = startAt + buf.duration;
      }
    } catch (e) {
      setStatus('error');
      setError(e?.message || 'Failed to start audio test');
    }
  }

  function stop() {
    // Idempotency guard — prevents double-close in React 18 strict-mode
    // where useEffect cleanup can fire twice on the same mounted component.
    if (stoppingRef.current) return;
    stoppingRef.current = true;

    try { wsRef.current?.send(JSON.stringify({ type: 'stop' })); } catch (_) {}
    try { wsRef.current?.close(); } catch (_) {}
    wsRef.current = null;

    try { mediaStreamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch (_) {}
    mediaStreamRef.current = null;

    // Guard: only close if AudioContext is not already closed.
    if (ctxRef.current && ctxRef.current.state !== 'closed') {
      try { ctxRef.current.close(); } catch (_) { /* already closed */ }
    }
    ctxRef.current = null;

    setStatus('closed');
  }

  useEffect(() => () => stop(), []);

  // Escape closes the modal (after gracefully stopping the audio session).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        stop();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Audio test conversation with bot"
      className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4"
    >
      <div className="panel w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="text-sm font-semibold text-white flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-civix-cyan" /> Audio test
          </div>
          <button onClick={() => { stop(); onClose?.(); }} aria-label="Close audio test" className="text-white/60 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {status === 'idle' && (
            <div className="space-y-3">
              <div className="text-sm text-white/70">
                Click <strong>Start</strong> to give microphone access and have a live audio conversation with your bot.
              </div>
              <button onClick={start} className="btn-primary flex items-center gap-2">
                <Mic className="w-4 h-4" /> Start
              </button>
            </div>
          )}
          {status === 'connecting' && (
            <div className="flex items-center gap-2 text-sm text-white/70">
              <Loader2 className="w-4 h-4 animate-spin" /> Connecting…
            </div>
          )}
          {(status === 'live' || status === 'closed') && (
            <div className="space-y-2">
              <div className="text-xs text-white/40">
                Spent ${Number(cost.spentUsd || 0).toFixed(3)} / cap ${Number(cost.capUsd || 0.5).toFixed(2)}
              </div>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {transcript.map((m, i) => (
                  <div key={i} className={`text-sm ${m.role === 'user' ? 'text-right' : ''}`}>
                    <span className={`inline-block rounded-lg px-3 py-1 ${m.role === 'user' ? 'bg-civix-cyan/20 text-white' : 'bg-white/10 text-white/90'}`}>
                      {m.text}
                    </span>
                  </div>
                ))}
              </div>
              {status === 'closed' && error && (
                <div className="text-xs text-yellow-300">Ended: {error}</div>
              )}
            </div>
          )}
          {status === 'error' && (
            <div className="text-sm text-red-300">Error: {error}</div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-white/10 flex justify-end gap-2">
          {status === 'live' && (
            <button onClick={stop} className="btn-ghost flex items-center gap-2">
              <MicOff className="w-4 h-4" /> Stop test
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
